/**
 * 🧹 **Placing a vefstofa clears what is in its way — D135.**
 *
 * 🎯 **haugur, 2026-10-05**, having tried to place one among birch and kjarr:
 * *"can we make it so the house just removes anything that is blocking it from
 * beeing placed?"* — then, asked: **anything**, and **gone** (like the earth
 * dug for the pit), **except things that hold stuff or are part of another
 * building**.
 *
 * | In the way | |
 * |---|---|
 * | scrub, heather, grass, birch, a stone or plank somebody placed, a torch, a wall | 🧹 cleared, and gone |
 * | a chest, a hearth, a bed, a manger, any part of another house | 🚫 refuses — they hold things, or a bigger thing would break |
 * | water | 🚫 refuses — a house does not dig a hole into a lake |
 *
 * ⚠️ **Gone, not into your pack**: otherwise placing and taking down a house
 * again and again would harvest a forest faster than an axe.
 *
 * 🌳 **A cleared birch trunk takes the rest of its trunk above the roof with
 * it**, or a stump of trunk would float over the house. The leaves stay, as
 * they do when a trunk is cut by hand.
 */

import { BLOCK, isFluid } from './blocks.ts';
import { burFrame, containerRulesOf, isBur, isBurPart, type BurFacing } from './bur.ts';
import { isHearthBlock } from './hearth.ts';
import { isPitBlock, ROOF_CLEAR } from './pithouse.ts';
import { isRum, isRumPart } from './rum.ts';
import { baseBlockOf } from './states.ts';

interface Cell {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/** 🚫 **Something placing must not clear** — it holds things, or it is part of a building. */
export function pitKeeps(id: number): boolean {
  return (
    containerRulesOf(id) !== undefined ||
    isHearthBlock(id) ||
    isRum(id) ||
    isRumPart(id) ||
    baseBlockOf(id) === BLOCK['jata'] ||
    isBur(id) ||
    isBurPart(id) ||
    isPitBlock(id)
  );
}

/** 🧹 May placing a vefstofa clear this block out of its way? */
export function pitClearable(id: number): boolean {
  return id !== 0 && !isFluid(id) && !pitKeeps(id);
}

/**
 * 🧹 **Every cell placing clears**: whatever is not air in the plot from the
 * ground up to the roof's clearance, the door's own cell included — and above
 * a cleared birch trunk, the rest of that trunk.
 *
 * ⚠️ Call it only for a plot `pitPlotVerdict` called `free` with
 * {@link pitClearable}: this lists, it does not judge.
 */
export function pitClearList(
  door: Cell,
  facing: BurFacing,
  blockAt: (x: number, y: number, z: number) => number,
): Cell[] {
  const { into, right } = burFrame(facing);
  const trunk = BLOCK['birkistofn'];
  const out: Cell[] = [];
  for (let d = 0; d <= 5; d++)
    for (let w = -3; w <= 3; w++) {
      const x = door.x + into.x * d + right.x * w;
      const z = door.z + into.z * d + right.z * w;
      let top = door.y + ROOF_CLEAR;
      for (let y = door.y; y <= top; y++) {
        const here = blockAt(x, y, z);
        if (here === 0) continue;
        out.push({ x, y, z });
        /* 🌳 A trunk reaching the top of the plot: follow it up. */
        if (here === trunk && y === top && blockAt(x, y + 1, z) === trunk) top += 1;
      }
    }
  return out;
}
